import { Injectable, inject } from '@angular/core';
import {
  UploadProgressEvent,
  VideoUploadResponse,
  VideoUploader,
} from '@api.video/video-uploader';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export interface ActiveUploadState {
  entityId: string;
  entityType: 'school' | 'job' | 'authority';
  uploader: VideoUploader | null;
}

@Injectable({
  providedIn: 'root',
})
export class ApiVideoService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/v1`;

  readonly defaultProgressValue: UploadProgressEvent = {
    uploadedBytes: 0,
    totalBytes: 0,
    currentChunk: 0,
    chunksCount: 0,
    chunksBytes: 0,
    currentChunkUploadedBytes: 0,
  };

  // Track active upload state
  private activeUpload: ActiveUploadState | null = null;
  private isUploadingSubject = new BehaviorSubject<boolean>(false);
  isUploading$ = this.isUploadingSubject.asObservable();

  private uploadProgressSubject = new BehaviorSubject<UploadProgressEvent>(
    this.defaultProgressValue
  );

  uploadProgress$ = this.uploadProgressSubject.asObservable();

  /**
   * Get the current active upload state
   */
  getActiveUpload(): ActiveUploadState | null {
    return this.activeUpload;
  }

  /**
   * Check if there's an active upload for a specific entity
   */
  isUploadActiveFor(
    entityId: string,
    entityType: 'school' | 'job' | 'authority'
  ): boolean {
    return (
      this.activeUpload !== null &&
      this.activeUpload.entityId === entityId &&
      this.activeUpload.entityType === entityType
    );
  }

  /**
   * Cancel the current upload if one is in progress
   */
  cancelUpload(): void {
    if (this.activeUpload?.uploader) {
      this.activeUpload.uploader.cancel();
      this.clearUploadState();
    }
  }

  /** Clears the progress bar (e.g. after register fails; success leaves 100% until next upload). */
  resetUploadProgressDisplay(): void {
    this.uploadProgressSubject.next(this.defaultProgressValue);
  }

  /**
   * Clear the upload state (called after upload completes or is cancelled)
   */
  private clearUploadState(): void {
    this.activeUpload = null;
    this.isUploadingSubject.next(false);
    this.uploadProgressSubject.next(this.defaultProgressValue);
  }

  /**
   * After bytes are uploaded to api.video, release the uploader but keep progress at 100%.
   * (Backend registration still runs in {@link MediaVideoService} — resetting progress here
   * used to show 0% until register finished.)
   */
  private releaseUploaderKeepProgress(): void {
    const cur = this.uploadProgressSubject.getValue();
    this.activeUpload = null;
    this.isUploadingSubject.next(false);
    if (cur.totalBytes > 0 && cur.uploadedBytes < cur.totalBytes) {
      this.uploadProgressSubject.next({
        ...cur,
        uploadedBytes: cur.totalBytes,
        currentChunkUploadedBytes: cur.chunksBytes || cur.totalBytes,
      });
    } else if (!cur.totalBytes) {
      this.uploadProgressSubject.next({
        uploadedBytes: 1,
        totalBytes: 1,
        currentChunk: 0,
        chunksCount: 0,
        chunksBytes: 0,
        currentChunkUploadedBytes: 0,
      });
    }
  }

  async uploadAsync(
    file: File,
    entityId: string,
    entityType: 'school' | 'job' | 'authority'
  ): Promise<VideoUploadResponse> {
    // Prevent concurrent uploads - cancel existing if any
    if (this.activeUpload) {
      console.warn(
        'Upload already in progress, cancelling previous upload for:',
        this.activeUpload.entityId
      );
      this.cancelUpload();
    }

    this.uploadProgressSubject.next(this.defaultProgressValue);
    this.isUploadingSubject.next(true);

    let uploadTokenUrl = '';
    if (entityType === 'school') {
      uploadTokenUrl = `${this.baseUrl}/schools/${entityId}/video/upload-token`;
    } else if (entityType === 'authority') {
      uploadTokenUrl = `${this.baseUrl}/school-authorities/${entityId}/video/upload-token`;
    } else {
      uploadTokenUrl = `${this.baseUrl}/job-postings/${entityId}/video/upload-token`;
    }

    const { token } = await lastValueFrom(
      this.http.post<{ token: string }>(uploadTokenUrl, {})
    );
    if (!token) {
      this.clearUploadState();
      throw new Error('No token');
    }

    const uploader = new VideoUploader({
      uploadToken: token,
      file,
    });

    // Store active upload state
    this.activeUpload = {
      entityId,
      entityType,
      uploader,
    };

    uploader.onProgress(e => {
      this.uploadProgressSubject.next(e);
    });

    try {
      const response = await uploader.upload();
      this.releaseUploaderKeepProgress();
      return response;
    } catch (e) {
      this.clearUploadState();
      throw e;
    }
  }

  registerSchoolVideo(schoolId: string, apiVideoId: string) {
    return this.http.post<{ id: string }>(
      `${this.baseUrl}/schools/${schoolId}/video/${apiVideoId}`,
      {}
    );
  }

  registerJobVideo(jobId: string, apiVideoId: string) {
    return this.http.post<{ id: string }>(
      `${this.baseUrl}/job-postings/${jobId}/video/${apiVideoId}`,
      {}
    );
  }

  registerAuthorityVideo(authorityId: string, apiVideoId: string) {
    return this.http.post<{ id: string }>(
      `${this.baseUrl}/school-authorities/${authorityId}/video/${apiVideoId}`,
      {}
    );
  }

  remove(videoId: string, schoolId: string) {
    return this.http.delete<void>(`${this.baseUrl}/schools/${schoolId}/video`);
  }

  removeJobVideo(videoId: string, jobId: string) {
    return this.http.delete<void>(
      `${this.baseUrl}/job-postings/${jobId}/video`
    );
  }

  removeAuthorityVideo(videoId: string, authorityId: string) {
    return this.http.delete<void>(
      `${this.baseUrl}/school-authorities/${authorityId}/video`
    );
  }
}
